import ModalBase from "../../../../connectui/component/modal/ModalBase";
import FlexContainer from "../../../../connectui/component/layout/FlexContainer";
import Text from "../../../../connectui/component/display/Text";
import React, {useContext} from "react";
import {useTheme} from "../../../../connectui/theme/ThemeProvider";
import OutlinedButtonBase from "../../../../connectui/component/input/button/base/OutlinedButtonBase";
import ButtonText from "../../../../connectui/component/input/button/ButtonText";
import ButtonBase from "../../../../connectui/component/input/button/base/ButtonBase";
import {buildStyles} from "../../../../connectui/component/buildStyles";
import OutlinedInputBase from "../../../../connectui/component/input/text/OutlinedInputBase";
import TextInput from "../../../../connectui/component/input/text/type/TextInput";
import RoundIcon from "../../../../connectui/component/display/RoundIcon";
import SvgIcon from "../../../../connectui/component/display/SvgIcon";
import ICONS from "../../../../connectui/icons/iconMap";
import DropDownSelect from "../../../../connectui/component/input/dropdown/DropDownSelect";
import OutlinedDropDownBase from "../../../../connectui/component/input/dropdown/base/OutlinedDropDownBase";
import {useDispatch} from "react-redux";
import {createAction} from "../../../../redux/actions/experiencesActions";
import {InboxContext} from "../../../../routes/MessagesPage";
import PhoneInput from "../../../../connectui/component/input/text/type/PhoneInput";
import {getGuest} from "../../../../redux/actions/guestsActions";
import {useTranslation} from "react-i18next";

const useStyles = (theme) =>
  buildStyles({
    inputIcon: {
      color: theme.colors.gray500,
      width: "20px",
      height: "20px",
      marginRight: theme.spacing(2),
    },
  });

function isValidEmail(email = "") {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidName(fullName = "") {
  const parts = fullName.trim().split(/\s+/);
  return parts.length >= 2;
}

export default function CreateContactModal({isOpen, onClose}) {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const [selectedContactType, setSelectedContactType] = React.useState("Guest");
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [phoneValid, setPhoneValid] = React.useState(false);

  const {setThreads, setAppliedFilters, setSearchText} =
    useContext(InboxContext);

  const [nameError, setNameError] = React.useState("");
  const [emailError, setEmailError] = React.useState("");
  const [phoneError, setPhoneError] = React.useState("");

  const handleConfirm = () => {
    let hasError = false;
    setNameError("");
    setEmailError("");
    setPhoneError("");

    if (!name) {
      setNameError(t("inbox.create-contact.name.error.required"));
      hasError = true;
    } else if (!isValidName(name)) {
      setNameError(t("inbox.create-contact.name.error.format"));
      hasError = true;
    }

    if (!email) {
      setEmailError(t("inbox.create-contact.email.error.required"));
      hasError = true;
    } else if (!isValidEmail(email)) {
      setEmailError(t("inbox.create-contact.email.error.format"));
      hasError = true;
    }

    if (!phoneValid && phone !== "") {
      setPhoneError(t("inbox.create-contact.phone.error"));
      hasError = true;
    }

    if (!hasError) {
      const action = {
        action_id: "update_guest",
        body: {
          conversation_stage: "todo",
          name,
          email,
          phone: phone !== "" ? phone : null,
          updated_at: new Date().getTime(),
          guest_type: selectedContactType.toLowerCase(),
        },
      };
      setSearchText("");
      setAppliedFilters([]);
      window.history.pushState({}, "", `${window.location.pathname}?view_id=0`);

      dispatch(
        createAction({
          trigger: null,
          action,
          onSuccess: (response) => {
            if (response?.action?.objects?.guest?.guest_id) {
              dispatch(
                getGuest(
                  response?.action?.objects?.guest?.guest_id,
                  (guest) => {
                    if (guest) {
                      setThreads((prev) => {
                        if (
                          prev.some(
                            (thread) => thread.guest_id === guest.guest_id,
                          )
                        ) {
                          return prev;
                        }
                        return [guest, ...prev];
                      });
                    }
                  },
                ),
              );
            }
          },
        }),
      );
      handleClose();
    }
  };

  const handleClose = () => {
    setName("");
    setEmail("");
    setPhone("");
    setNameError("");
    setEmailError("");
    setPhoneError("");
    setSelectedContactType("Guest");
    setPhoneValid(false);
    onClose(null);
  };

  function getContactType(type, label = false) {
    let icon = null;
    if (type === "Guest") icon = ICONS.user;
    else if (type === "Owner") icon = ICONS["home-owner"];
    else if (type === "Cleaner") icon = ICONS.clean;
    else if (type === "Maintenance") icon = ICONS.tool;
    return (
      <FlexContainer
        justifyContent="space-between"
        alignItems="center"
        sx={{width: "100%"}}
        key={type}
        value={type}
      >
        <FlexContainer alignItems="center">
          <SvgIcon component={icon} className={classes.inputIcon} />
          <Text variant="textMD" color="textSecondary">
            {type}
          </Text>
        </FlexContainer>
        {!label && selectedContactType === type && (
          <SvgIcon
            component={ICONS.check}
            sx={{width: "20px", height: "20px", color: theme.colors.primary800}}
          />
        )}
      </FlexContainer>
    );
  }

  return (
    <ModalBase isOpen={isOpen} onClose={handleClose}>
      <FlexContainer
        flexDirection="column"
        alignItems="flex-start"
        gap={theme.spacing(5)}
        sx={{padding: theme.spacing(2), maxWidth: "450px"}}
      >
        <RoundIcon
          iconComponent="user-plus"
          baseColor="primary"
          sx={{margin: theme.spacing(-1, 0, 0, -1)}}
        />
        <FlexContainer
          flexDirection="column"
          gap={theme.spacing(2)}
          sx={{width: "100%"}}
        >
          <Text variant="textLG" weight="semibold">
            {t("inbox.create-contact.title")}
          </Text>
          <Text variant="textSM" color="gray600">
            {t("inbox.create-contact.subtitle")}
          </Text>
        </FlexContainer>

        <FlexContainer
          flexDirection="column"
          gap={theme.spacing(3)}
          sx={{width: "100%"}}
        >
          <FlexContainer
            flexDirection="column"
            gap={theme.spacing(1.5)}
            sx={{width: "100%"}}
          >
            <Text variant="textSM" weight="medium" color="gray700">
              {t("inbox.create-contact.contact-type")}
            </Text>
            <DropDownSelect
              dropDownBase={OutlinedDropDownBase}
              onChange={(e) => setSelectedContactType(e)}
              customLabel={getContactType(selectedContactType, true)}
            >
              {getContactType("Guest")}
              {getContactType("Owner")}
              {getContactType("Cleaner")}
              {getContactType("Maintenance")}
            </DropDownSelect>
          </FlexContainer>
          <FlexContainer
            flexDirection="column"
            gap={theme.spacing(1.5)}
            sx={{width: "100%"}}
          >
            <Text variant="textSM" weight="medium" color="gray700">
              {t("inbox.create-contact.name.label")}
            </Text>
            <TextInput
              inputBase={OutlinedInputBase}
              startIcon={
                <SvgIcon component={ICONS.user} className={classes.inputIcon} />
              }
              inputProps={{
                placeholder: t("inbox.create-contact.name.placeholder"),
                value: name,
                onChange: (e) => setName(e.target.value),
              }}
              sx={{
                border: nameError
                  ? "1px solid red"
                  : "1px solid " + theme.colors.gray300,
              }}
            />
            {nameError && (
              <Text variant="textSM" sx={{color: theme.colors.error}}>
                {nameError}
              </Text>
            )}
          </FlexContainer>
          <FlexContainer
            flexDirection="column"
            gap={theme.spacing(1.5)}
            sx={{width: "100%"}}
          >
            <Text variant="textSM" weight="medium" color="gray700">
              {t("inbox.create-contact.phone.label")}
            </Text>

            <PhoneInput
              inputBase={OutlinedInputBase}
              edit
              phone={phone}
              onChange={(value) => {
                setPhone(value);
              }}
              setValidPhone={(valid) => {
                setPhoneValid(valid);
              }}
              error={phoneError !== ""}
            />
            {phoneError && (
              <Text variant="textSM" sx={{color: theme.colors.error}}>
                {phoneError}
              </Text>
            )}
          </FlexContainer>
          <FlexContainer
            flexDirection="column"
            gap={theme.spacing(1.5)}
            sx={{width: "100%"}}
          >
            <Text variant="textSM" weight="medium" color="gray700">
              {t("inbox.create-contact.email.label")}
            </Text>
            <TextInput
              inputBase={OutlinedInputBase}
              startIcon={
                <SvgIcon component={ICONS.mail} className={classes.inputIcon} />
              }
              inputProps={{
                placeholder: t("inbox.create-contact.email.placeholder"),
                value: email,
                onChange: (e) => setEmail(e.target.value),
              }}
              sx={{
                border: emailError
                  ? "1px solid red"
                  : "1px solid " + theme.colors.gray300,
              }}
            />
            {emailError && (
              <Text variant="textSM" sx={{color: theme.colors.error}}>
                {emailError}
              </Text>
            )}
          </FlexContainer>
        </FlexContainer>

        <FlexContainer
          gap={theme.spacing(3)}
          justifyContent="space-between"
          sx={{width: "100%", marginTop: theme.spacing(3)}}
        >
          <ButtonText
            buttonBase={OutlinedButtonBase}
            label={t("inbox.create-contact.buttons.cancel")}
            sx={{width: "100%", flex: 1}}
            onClick={handleClose}
          />
          <ButtonText
            buttonBase={ButtonBase}
            label={t("inbox.create-contact.buttons.confirm")}
            color="white"
            sx={{width: "100%", flex: 1}}
            onClick={handleConfirm}
          />
        </FlexContainer>
      </FlexContainer>
    </ModalBase>
  );
}
